import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";

/**
 * Two-column checklist. Used on service pages for "What's included".
 * groups: [{ title, items: [...strings] }]
 */
export const ChecklistSection = ({
  eyebrow,
  heading,
  subheading,
  groups = [],
  background = "white",
}) => {
  const bgClass = background === "slate" ? "bg-slate-50" : "bg-white";

  return (
    <section
      className={`py-20 md:py-28 ${bgClass}`}
      data-testid="checklist-section"
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mb-14"
        >
          {eyebrow && (
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">
              {eyebrow}
            </span>
          )}
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-navy mt-3 mb-4">
            {heading}
          </h2>
          {subheading && (
            <p className="text-slate-600 text-lg leading-relaxed">
              {subheading}
            </p>
          )}
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group, gi) => (
            <motion.div
              key={group.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: gi * 0.08 }}
              className={`rounded-2xl p-7 ${
                background === "slate"
                  ? "bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                  : "bg-slate-50"
              }`}
              data-testid={`checklist-group-${gi}`}
            >
              <h3 className="font-heading text-xl font-semibold text-navy mb-5 flex items-center gap-2">
                <span className="w-8 h-8 bg-gold/15 rounded-lg flex items-center justify-center text-gold-dark text-sm font-bold">
                  {gi + 1}
                </span>
                {group.title}
              </h3>
              <ul className="space-y-2.5">
                {group.items.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed"
                  >
                    <CheckCircle className="w-4 h-4 text-teal flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
